import {
  FRAME_STATE_HINT,
  type FrameState,
} from '../../src/domain/pose/frameState.ts';
import {
  BONES,
  JOINTS,
  KEYPOINT_COUNT,
  STRIDE,
} from '../../src/domain/pose/halpe26.ts';
import { startMockSender } from './mock.ts';
import {
  CAST_NAMESPACE,
  TV_PROTOCOL_VERSION,
  isTvMessage,
  unpackKeypoints,
  type TvMessage,
} from '../../src/domain/tv/protocol.ts';

/**
 * TV ALICISI
 * ===========================================================================
 * Telefondan gelen iskelet, skor ve kadraj mesajlarını TV ekranına çizer.
 * Cast Web Receiver, Tizen ve webOS uygulamalarının ÜÇÜ DE bu sayfayı
 * çalıştıracak — değişen yalnızca paketleme ve mesajların hangi kanaldan
 * geldiği olacak. O yüzden burada Cast'e özgü tek satır yok: dışarıya
 * `window.dwReceiver.handleMessage` açılıyor, taşıma katmanı onu besliyor.
 *
 * Şu an sahte bir sürücü besliyor, yani sayfa tarayıcıda tek başına çalışıyor.
 * Ne Cast hesabı ne cihaz gerekiyor; alıcının tamamı böyle geliştirilebilir.
 *
 * Kaynak paylaşımı: iskelet tanımı ve mesaj sözleşmesi `src/domain` altından
 * OLDUĞU GİBİ import ediliyor. Telefon ile TV'nin aynı geometriyi konuşması
 * bu yüzden garanti — iki yerde ayrı tanım tutulmuyor.
 */

const BONE_COLOR = '#6CD5B8';
const JOINT_COLOR = '#DCE258';

/**
 * Yumuşatma katsayısı. Telefon saniyede 12-15 poz yolluyor, TV 60 Hz
 * çiziyor; arada bir şey yapılmazsa iskelet kademeli zıplar. Her karede
 * hedefe bu oranda yaklaşmak, zaman damgalı interpolasyondan çok daha basit
 * ve ağ jitter'ına karşı da dayanıklı.
 */
const SMOOTHING = 0.35;

type State = {
  /** En son GELEN poz (0..1 ondalık, 26 x 3). */
  target: number[];
  /** Ekrana çizilen, yumuşatılmış poz. */
  shown: number[];
  /** Henüz hiç poz gelmediyse çizim yapılmaz. */
  hasPose: boolean;
  frame: FrameState;
  score: number;
  combo: number;
  rating?: string;
  ratingUntil: number;
  lastMessageAt: number;
};

const state: State = {
  target: new Array<number>(KEYPOINT_COUNT * STRIDE).fill(0),
  shown: new Array<number>(KEYPOINT_COUNT * STRIDE).fill(0),
  hasPose: false,
  frame: 'no_person',
  score: 0,
  combo: 0,
  ratingUntil: 0,
  lastMessageAt: 0,
};

/** Taşıma katmanı bunu çağırır. Cast, WebSocket, sahte sürücü — hepsi aynı kapı. */
const handleMessage = (raw: unknown): void => {
  if (!isTvMessage(raw)) return;
  const message: TvMessage = raw;
  state.lastMessageAt = Date.now();

  switch (message.t) {
    case 'pose': {
      state.target = unpackKeypoints(message.p);
      if (!state.hasPose) {
        // İlk pozu yumuşatmadan yerleştir, yoksa iskelet sol üstten uçarak gelir.
        state.shown = [...state.target];
        state.hasPose = true;
      }
      break;
    }
    case 'frame': {
      state.frame = message.state;
      break;
    }
    case 'score': {
      state.score = message.total;
      state.combo = message.combo;
      if (message.rating != null) {
        state.rating = message.rating;
        state.ratingUntil = Date.now() + 700;
      }
      break;
    }
    default:
      break;
  }
};

// --- Çizim ---------------------------------------------------------------

const canvas = document.getElementById('skeleton') as HTMLCanvasElement;
const context = canvas.getContext('2d');

const scoreValue = document.getElementById('score-value') as HTMLElement;
const comboValue = document.getElementById('combo-value') as HTMLElement;
const ratingEl = document.getElementById('rating') as HTMLElement;
const hintEl = document.getElementById('hint') as HTMLElement;
const versionEl = document.getElementById('version') as HTMLElement;

versionEl.textContent = `v${TV_PROTOCOL_VERSION}`;

/**
 * Tuvali kutunun gerçek pikseline göre ölçekler. TV'lerde devicePixelRatio
 * 1 olsa da bu adım atlanırsa CSS boyutu ile tampon boyutu ayrışıp iskelet
 * bulanık çıkıyor.
 */
const resize = (): void => {
  const ratio = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = Math.round(rect.width * ratio);
  canvas.height = Math.round(rect.height * ratio);
};

const draw = (): void => {
  if (context == null) return;
  const { width, height } = canvas;
  context.clearRect(0, 0, width, height);
  if (!state.hasPose) return;

  // Hedefe yaklaş.
  for (let i = 0; i < state.shown.length; i += 1) {
    const from = state.shown[i] ?? 0;
    const to = state.target[i] ?? 0;
    state.shown[i] = from + (to - from) * SMOOTHING;
  }

  const px = (k: number): number => (state.shown[k * STRIDE] ?? 0) * width;
  const py = (k: number): number => (state.shown[k * STRIDE + 1] ?? 0) * height;
  const vis = (k: number): number => state.shown[k * STRIDE + 2] ?? 0;

  const scale = Math.min(width, height);
  context.lineWidth = Math.max(2, scale * 0.012);
  context.lineCap = 'round';
  context.lineJoin = 'round';
  context.strokeStyle = BONE_COLOR;

  context.beginPath();
  for (const bone of BONES) {
    const [a, b] = bone;
    if (vis(a) < 0.3 || vis(b) < 0.3) continue;
    context.moveTo(px(a), py(a));
    context.lineTo(px(b), py(b));
  }
  context.stroke();

  context.fillStyle = JOINT_COLOR;
  const radius = Math.max(3, scale * 0.016);
  for (const k of JOINTS) {
    if (vis(k) < 0.3) continue;
    context.beginPath();
    context.arc(px(k), py(k), radius, 0, Math.PI * 2);
    context.fill();
  }
};

const paintHud = (): void => {
  scoreValue.textContent = String(Math.round(state.score));
  comboValue.textContent = state.combo > 0 ? `x${state.combo}` : '';

  const hint = FRAME_STATE_HINT[state.frame];
  hintEl.textContent = hint;
  hintEl.style.opacity = hint === '' ? '0' : '1';

  const showRating = state.rating != null && Date.now() < state.ratingUntil;
  ratingEl.textContent = showRating ? (state.rating ?? '') : '';
  ratingEl.style.opacity = showRating ? '1' : '0';
};

const loop = (): void => {
  draw();
  paintHud();
  window.requestAnimationFrame(loop);
};

window.addEventListener('resize', resize);
resize();
window.requestAnimationFrame(loop);

// Taşıma katmanı için tek giriş noktası.
(window as unknown as { dwReceiver: unknown }).dwReceiver = { handleMessage };

// --- Taşıma katmanı ------------------------------------------------------

/** CAF SDK'sının ihtiyacımız olan kadarı — tam tipler için ek paket gerekmiyor. */
type CastFramework = {
  CastReceiverContext?: {
    getInstance: () => {
      addCustomMessageListener: (
        namespace: string,
        handler: (event: { data: unknown }) => void,
      ) => void;
      start: () => void;
    };
  };
};

/**
 * Sayfa gerçek bir Cast cihazında mı çalışıyor.
 *
 * Chromecast ve Android TV alıcıları user agent'a `CrKey` koyuyor. Bu kontrol
 * olmadan normal tarayıcıda da Cast yolu denenirdi: SDK yüklenir, `start()`
 * sessizce hiçbir şey yapmaz ve sahte sürücü de devreye girmediği için ekran
 * boş kalırdı.
 */
const isCastDevice = (): boolean =>
  typeof navigator !== 'undefined' && navigator.userAgent.includes('CrKey');

const startCast = (): boolean => {
  const framework = (window as unknown as { cast?: { framework?: CastFramework } })
    .cast?.framework;
  const contextClass = framework?.CastReceiverContext;
  if (contextClass == null) return false;
  try {
    const context = contextClass.getInstance();
    context.addCustomMessageListener(CAST_NAMESPACE, (event) => {
      handleMessage(event.data);
    });
    context.start();
    return true;
  } catch {
    return false;
  }
};

/**
 * Varsayılan davranış otomatik: Cast cihazındaysak Cast kanalı, değilsek
 * sahte sürücü. `?mock=1` zorla sahte, `?mock=0` zorla gerçek (Cast yolunu
 * cihazda denerken sahte verinin araya girmemesi için).
 */
const mockParam = new URLSearchParams(window.location.search).get('mock');
const useCast = mockParam !== '1' && isCastDevice() && startCast();
if (!useCast && mockParam !== '0') {
  startMockSender(handleMessage);
}
